"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GalaAnalytics = void 0;
const dtos_1 = require("../../../gal-dtos/index");
const analytics_1 = require("../../../gal-dtos/types/analytics");
const logger_1 = require("../class/logger");
const config_1 = __importDefault(require("../config"));
const session_1 = require("./session");
const exposed_constants_1 = require("./exposed-constants");
const report_queue_1 = require("./report-queue");
const reporting_1 = require("./reporting");
const user_persistence_1 = require("./user-persistence");
class GalaAnalytics {
    constructor(args) {
        var _a, _b;
        Object.defineProperty(this, "args", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: args
        });
        Object.defineProperty(this, "isDestroyed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isEnabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "reportQueue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "session", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "reporting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.validateArgs(args);
        this.reporting = this.createReporter(args);
        this.enabled = (_a = args.enabled) !== null && _a !== void 0 ? _a : true;
        this.session = session_1.Session.load((dto) => this.onSendReport(dto, true, true), args.sessionPingIntervalMS, args.disableAutoSessionHandling);
        if (args.user) {
            user_persistence_1.UserPersistence.user = args.user;
        }
        if ((_b = args.disabledQueue) !== null && _b !== void 0 ? _b : true) {
            this.reportQueue = new report_queue_1.ReportQueue();
        }
    }
    async deregister() {
        this.isDestroyed = true;
        await this.session.deregister();
    }
    get enabled() {
        return this.isEnabled;
    }
    set enabled(isEnabled) {
        if (!isEnabled && this.isEnabled) {
            logger_1.Logger.error('Cannot disable reporting once it has been enabled');
            return;
        }
        this.isEnabled = isEnabled;
        if (this.isEnabled) {
            logger_1.Logger.info('Enabled');
            this.processQueue();
        }
    }
    clearReportQueue() {
        logger_1.Logger.info('Report queue flushed');
        this.reportQueue.clear();
    }
    report(payload, asBeacon = false) {
        return this.onSendReport(payload, asBeacon);
    }
    setUserId(userId) {
        user_persistence_1.UserPersistence.user = userId;
        logger_1.Logger.info('User id set');
    }
    setSessionId(sessionId) {
        this.session.setId(sessionId);
    }
    clearUserId() {
        user_persistence_1.UserPersistence.user = null;
        logger_1.Logger.info('User id unset');
    }
    static allowedGroups(type) {
        var _a, _b;
        return (_b = (_a = dtos_1.dtos[type]) === null || _a === void 0 ? void 0 : _a.allowedGroups()) !== null && _b !== void 0 ? _b : [];
    }
    static payloadToInstance(type, payload) {
        const cls = dtos_1.dtos[type];
        return cls.toInstance(payload);
    }
    static validateType(type) {
        if (!type)
            return;
        if (!Object.values(dtos_1.GalEvents).includes(type)) {
            throw new Error(`Could not submit report, invalid event type: ${type}`);
        }
    }
    static async validateDTO(payload) {
        let validationResult;
        try {
            validationResult = await dtos_1.GalBaseDTO.validate(payload);
        }
        catch (err) {
            logger_1.Logger.handle(err);
            throw new Error('Malformed payload');
        }
        if (!validationResult.valid) {
            throw new Error(validationResult.errors.map((e) => `.details: ${e}`).join('; '));
        }
    }
    async onSendReport(payload, asBeacon = false, isInternal = false) {
        var _a, _b;
        if (this.isDestroyed && !isInternal) {
            logger_1.Logger.warn(`Previously destroyed by deregistration`);
            return;
        }
        GalaAnalytics.validateType(payload.type);
        if (!isInternal && !this.checkValidDTO(payload.type)) {
            return;
        }
        payload = GalaAnalytics.payloadToInstance(payload.type, dtos_1.GalBaseDTO.toPlain(payload));
        await GalaAnalytics.validateDTO(payload);
        const reportPayload = {
            details: payload,
            user: (_a = user_persistence_1.UserPersistence.user) !== null && _a !== void 0 ? _a : '',
            timestamp: Date.now(),
            session: this.session.id,
            elapsed: this.session.elapsedMS,
            deviceId: this.args.deviceId,
            advertisingId: this.args.advertisingId,
            environment: this.args.environment,
            gameId: this.args.gameId,
            systemSpecs: this.args.systemSpecs,
        };
        if (this.isEnabled) {
            await this.reporting.send(reportPayload, this.session.elapsedMS, asBeacon);
        }
        else {
            if ((_b = this.args.disabledQueue) !== null && _b !== void 0 ? _b : true) {
                logger_1.Logger.info(`Report type ${payload.type} queued`);
            }
            this.reportQueue.enqueue(reportPayload);
        }
        if (payload.type === dtos_1.GalEvents.LogoutDTO) {
            user_persistence_1.UserPersistence.clearUser();
        }
    }
    async processQueue() {
        var _a, _b;
        while ((_a = this.reportQueue) === null || _a === void 0 ? void 0 : _a.hasNext) {
            const next = this.reportQueue.next();
            if (next) {
                next.user = (_b = user_persistence_1.UserPersistence.user) !== null && _b !== void 0 ? _b : next === null || next === void 0 ? void 0 : next.user;
                await this.reporting.send(next, this.session.elapsedMS);
            }
            await new Promise((resolve) => setTimeout(resolve, config_1.default.eventQueueProcessBackoffMS));
        }
    }
    validateArgs(args) {
        if (args.environment &&
            !Object.values(analytics_1.GalEnvironment).includes(args.environment)) {
            throw new Error(`Environment must be ${Object.values(analytics_1.GalEnvironment).join(',')}`);
        }
    }
    createReporter(args) {
        var _a, _b, _c, _d, _e;
        return new reporting_1.GalReport({
            reportingUri: args.reportingUri,
            publisherId: args.publisherId,
            reportingGroup: args.publisherGroup,
            obscure: args.useObscureEndpoint !== false,
            environment: (_a = args.environment) !== null && _a !== void 0 ? _a : undefined,
            fpEndpoint: args.fpEndpoint,
            failOnReportError: (_b = args.failOnReportError) !== null && _b !== void 0 ? _b : false,
            gameId: (_c = args.gameId) !== null && _c !== void 0 ? _c : '',
            deviceId: (_d = args.deviceId) !== null && _d !== void 0 ? _d : '',
            advertisingId: (_e = args.advertisingId) !== null && _e !== void 0 ? _e : '',
        });
    }
    checkValidDTO(type) {
        var _a;
        if (this.isSessionType(type)) {
            if (!this.args.disableAutoSessionHandling) {
                logger_1.Logger.warn(`A session report overlaps internal methods. Disable auto session handling to rectify.`);
                return false;
            }
            else if (!((_a = this.session.id) === null || _a === void 0 ? void 0 : _a.length)) {
                logger_1.Logger.warn(`Session report without setting instance sessionId, invalid.`);
                return false;
            }
        }
        return true;
    }
    isSessionType(type) {
        return ['SessionStartDTO', 'SessionEndDTO', 'SessionPingDTO'].includes(type);
    }
}
exports.GalaAnalytics = GalaAnalytics;
Object.defineProperty(GalaAnalytics, "constants", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: exposed_constants_1.ExposedConstants
});
